import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { User } from '@/types';
import { authApi } from '@/lib/api';

// Helpers para leer/escribir tokens independientemente del storage usado.
// Solo se guarda el ACCESS token (vida 15m): el refresh vive en la cookie
// httpOnly `boosters_ai_refresh`, fuera del alcance de cualquier script.
export function getStoredToken(key: string): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(key) ?? sessionStorage.getItem(key);
}

export function clearStoredTokens() {
  if (typeof window === 'undefined') return;
  // `refresh_token` e `impersonate_backup` se limpian por las sesiones
  // abiertas antes de mover el refresh a la cookie httpOnly.
  ['access_token', 'refresh_token', 'impersonate_backup'].forEach(k => {
    localStorage.removeItem(k);
    sessionStorage.removeItem(k);
  });
}

/// Flag module-level (NO persistido) que evita que `checkAuth()` corra
/// más de una vez por carga del bundle. `ProtectedRoute` se remonta en
/// cada navegación porque `DashboardLayout` vive dentro de cada page;
/// sin este guard, cada click en el sidebar disparaba un GET
/// /api/auth/me extra que en offline se acumulaba como petición fallida.
/// login/logout/register lo resetan para forzar re-validación.
let authCheckedThisSession = false;
function resetAuthCheck() {
  authCheckedThisSession = false;
}

/// Permite forzar una re-verificación de la sesión saltándose el guard
/// de `authCheckedThisSession`. Útil para timers que validan la sesión
/// periódicamente (ver `useAuthRefresh` en hooks/).
export function forceRecheckAuth() {
  authCheckedThisSession = false;
}

/// Limpia el cache persistido de React Query, el snapshot del propio
/// authStore en localStorage Y la cache del SW. Necesario en logout
/// para que datos sensibles (usuarios, notificaciones, info del tenant)
/// no queden en el dispositivo accesibles tras cerrar sesión —
/// especialmente importante en dispositivos compartidos.
///
/// Limpia también:
///   - `boosters_ai_learned_apis` (lista de endpoints aprendidos por el
///     `api-discovery` para warmup; pertenecía al usuario saliente).
///   - Cache HTTP del SW (envía `clear-cache` al SW activo).
///
/// Emite `boosters-ai:client-state-cleared` para que `providers` llame
/// `queryClient.clear()` y deje también la cache en memoria vacía.
function clearPersistedClientState() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem('boosters-ai-query-cache');
    window.localStorage.removeItem('boosters-ai-auth');
    window.localStorage.removeItem('boosters_ai_learned_apis');
    // Pide al SW que purgue su API cache. El SW responde silenciosamente
    // si está disponible. Si no hay SW, no pasa nada.
    if (navigator.serviceWorker?.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'clear-cache' });
    }
    window.dispatchEvent(new Event('boosters-ai:client-state-cleared'));
  } catch {
    // localStorage no disponible — nada que limpiar.
  }
}

/** `true` si la sesión actual es una impersonación (el backend marca el
 *  `user` con `impersonatorId`). Ya no hay backup de tokens en el cliente:
 *  la vuelta se hace server-side con la cookie de refresh. */
export function isImpersonating(): boolean {
  return !!useAuthStore.getState().user?.impersonatorId;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  avatarVersion: number;
  login: (email: string, password: string, rememberMe: boolean) => Promise<void>;
  loginWithGoogle: (idToken: string, rememberMe: boolean) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  setUser: (user: User) => void;
  bumpAvatarVersion: () => void;
  startImpersonation: (userId: string) => Promise<void>;
  stopImpersonation: () => Promise<void>;
}

/// Clave del storage donde persistimos el snapshot del usuario. Solo
/// guardamos `user` y `isAuthenticated`; el `isLoading` siempre arranca
/// en `true` para que el splash inicial corra como antes. La sesión
/// real se valida con el access token y el `checkAuth()` del montado.
///
/// Por qué persistir el `user`: tras un F5 offline, el access token
/// sigue en localStorage pero `getProfile()` falla por red. Sin este
/// snapshot, el store quedaría con `user: null` → muchas partes de la
/// UI no funcionan (sidebar con tenant, permisos, avatar, dashboards
/// per-tenant…). Con el snapshot, la app sigue navegable offline; el
/// `user` se refresca silenciosamente cuando vuelva la red.
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  avatarVersion: 0,

  login: async (email: string, password: string, rememberMe: boolean) => {
    // `rememberMe` viaja al backend: decide si la cookie httpOnly del
    // refresh es persistente (7d) o de sesión.
    const response = await authApi.login({ email, password, rememberMe });
    const storage = rememberMe ? localStorage : sessionStorage;
    storage.setItem('access_token', response.accessToken);
    resetAuthCheck();
    set({ user: response.user, isAuthenticated: true });
  },

  loginWithGoogle: async (idToken: string, rememberMe: boolean) => {
    const response = await authApi.googleLogin(idToken, rememberMe);
    const storage = rememberMe ? localStorage : sessionStorage;
    storage.setItem('access_token', response.accessToken);
    set({ user: response.user, isAuthenticated: true });
  },

  register: async (name: string, email: string, password: string) => {
    const response = await authApi.register({ name, email, password });
    localStorage.setItem('access_token', response.accessToken);
    set({ user: response.user, isAuthenticated: true });
  },

  logout: async () => {
    await authApi.logout();
    clearStoredTokens();
    resetAuthCheck();
    clearPersistedClientState();
    set({ user: null, isAuthenticated: false });
  },

  setUser: (user: User) => set({ user }),

  bumpAvatarVersion: () => set((state) => ({ avatarVersion: state.avatarVersion + 1 })),

  startImpersonation: async (userId: string) => {
    // Bloquear impersonación anidada.
    if (useAuthStore.getState().user?.impersonatorId) {
      throw new Error('Ya estás en una sesión de impersonación');
    }
    const response = await authApi.impersonate(userId);
    // La cookie httpOnly del refresh queda sustituida por la de la sesión
    // impersonada (nunca persistente). El access va a sessionStorage.
    sessionStorage.setItem('access_token', response.accessToken);
    localStorage.removeItem('access_token');
    set({ user: response.user, isAuthenticated: true });
  },

  stopImpersonation: async () => {
    // La vuelta es server-side: el refresh de la sesión impersonada lleva el
    // `impersonatorId`; el backend la revoca y abre sesión de la cuenta
    // original. El cliente NUNCA guarda los tokens del impersonador (antes un
    // XSS durante la impersonación se llevaba credenciales de super-admin).
    const response = await authApi.stopImpersonation();
    sessionStorage.removeItem('viewing_tenant');
    clearStoredTokens();
    sessionStorage.setItem('access_token', response.accessToken);
    // Reseteamos el flag de auth check y el persist para que la próxima
    // navegación rehydrate consistente con el user restaurado.
    resetAuthCheck();
    set({ user: response.user, isAuthenticated: true });
  },

  checkAuth: async () => {
    // Deduplicación: `ProtectedRoute` está dentro de `DashboardLayout`
    // que se monta en CADA page (no en un layout compartido), así que
    // cada navegación cliente-side remontaría ProtectedRoute → otro
    // `checkAuth()` → otra petición `GET /api/auth/me`. Eso generaba
    // un pico de peticiones por cada click en el sidebar. Con este
    // guard, `checkAuth` corre una sola vez por carga del bundle.
    // Las funciones login/logout/register resetan el flag para
    // forzar la re-verificación cuando cambie la sesión.
    if (authCheckedThisSession) return;
    authCheckedThisSession = true;

    const token = getStoredToken('access_token');
    if (!token) {
      set({ isLoading: false, isAuthenticated: false });
      return;
    }

    try {
      const user = await authApi.getProfile();
      set({ user, isAuthenticated: true, isLoading: false });
    } catch (err: unknown) {
      // Solo borrar tokens y desautenticar en errores de auth
      // explícitos (401/403). Errores de red (offline, backend caído,
      // 5xx) NO deben tirar la sesión — el access token sigue siendo
      // válido en cliente; cuando vuelva la red, el siguiente refetch
      // de getProfile lo confirmará. Mantener `isAuthenticated: true`
      // evita que `ProtectedRoute` redirija a /login mientras navegas
      // offline.
      const status =
        typeof err === 'object' && err !== null && 'response' in err
          ? (err as { response?: { status?: number } }).response?.status
          : undefined;
      if (status === 401 || status === 403) {
        clearStoredTokens();
        set({ user: null, isAuthenticated: false, isLoading: false });
      } else {
        // Mantenemos `isAuthenticated: true` para que la app siga
        // accesible offline. Si nunca conseguimos el `user` (primera
        // carga sin red), `user` queda null y los componentes que
        // dependen de él muestran skeletons; la UI sigue navegable.
        set({ isLoading: false, isAuthenticated: true });
      }
    }
  },
    }),
    {
      name: 'boosters-ai-auth',
      storage: createJSONStorage(() =>
        typeof window === 'undefined'
          ? (undefined as unknown as Storage)
          : window.localStorage,
      ),
      // Solo persisto el snapshot del usuario y el flag de sesión.
      // `isLoading` debe arrancar en `true` cada nueva sesión.
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      // Si en el snapshot persistido tenemos `user`, ya estamos
      // efectivamente autenticados — `isLoading` puede arrancar en
      // `false` y la app salta el splash. Si no hay snapshot, esto
      // no se llama y `isLoading` se queda en `true` (initial state).
      onRehydrateStorage: () => (state) => {
        if (state?.user) state.isLoading = false;
      },
    },
  ),
);
